import { useId } from 'react';

import logo from '@/assets/trumio-logo.png';

import { cn } from '../lib/cn';

/** Original Trumio letterforms, cropped to the wordmark only. */
export function Logo({
  className,
  height = 24,
  variant = 'auto',
}: {
  className?: string;
  height?: number;
  variant?: 'auto' | 'inverse';
}) {
  const maskId = `trumio-wordmark-${useId()}`;
  return (
    <svg
      role="img"
      aria-label="Trumio"
      viewBox="202 15 606 144"
      width={606}
      height={144}
      style={{ height, width: height * (606 / 144), aspectRatio: '606 / 144' }}
      className={cn('trumio-logo block max-w-none shrink-0 select-none', className)}
      data-variant={variant}
      focusable="false"
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x="202"
          y="15"
          width="606"
          height="144"
          style={{ maskType: 'alpha' }}
        >
          <image href={logo} width="808" height="248" />
        </mask>
      </defs>
      <rect
        x="202"
        y="15"
        width="606"
        height="144"
        fill="var(--logo-wordmark)"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}

/** Alpha bounds of the geometric symbol inside the same artwork, padded to a square. */
const MARK_CROP = { x: 4, y: 11, width: 162, height: 188 } as const;
const MARK_VIEW_BOX = `${MARK_CROP.x - (MARK_CROP.height - MARK_CROP.width) / 2} ${MARK_CROP.y} ${MARK_CROP.height} ${MARK_CROP.height}`;

/**
 * The original geometric symbol, painted in the wordmark ink like the lettering.
 * Pass `decorative` when the wordmark next to it already names the brand.
 */
export function LogoMark({
  className,
  size = 24,
  decorative = false,
}: {
  className?: string;
  size?: number;
  decorative?: boolean;
}) {
  const maskId = `trumio-mark-${useId()}`;
  return (
    <svg
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': 'Trumio' })}
      viewBox={MARK_VIEW_BOX}
      width={MARK_CROP.height}
      height={MARK_CROP.height}
      style={{ width: size, height: size, aspectRatio: '1 / 1' }}
      className={cn('trumio-mark block shrink-0 select-none', className)}
      focusable="false"
    >
      <defs>
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={MARK_CROP.x}
          y={MARK_CROP.y}
          width={MARK_CROP.width}
          height={MARK_CROP.height}
          style={{ maskType: 'alpha' }}
        >
          <image href={logo} width="808" height="248" />
        </mask>
      </defs>
      <rect
        x={MARK_CROP.x}
        y={MARK_CROP.y}
        width={MARK_CROP.width}
        height={MARK_CROP.height}
        fill="var(--logo-wordmark)"
        mask={`url(#${maskId})`}
      />
    </svg>
  );
}
